import { useId, useMemo } from "react";

import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

import EmptyState from "../EmptyState";

type ChartPoint = {
  label: string;

  /** null = hueco (sin datos en ese bucket): la curva se corta en vez de interpolar. */

  value: number | null;
};

type Palette = {
  border: string;

  background: string;

  point?: string;
};

type TimeSeriesChartCardProps = {
  title: string;

  points: ChartPoint[];

  palette: Palette;

  valueSuffix?: string;

  emptyTitle?: string;

  emptyDescription?: string;

  height?: number;
};

const TimeSeriesChartCard = ({
  title,

  points,

  palette,

  valueSuffix,

  emptyTitle = "Sin datos",

  emptyDescription = "Aún no hay registros para este período.",

  height = 300,
}: TimeSeriesChartCardProps) => {
  // Id de gradiente único y SIN caracteres especiales: derivarlo del título

  // rompía el funcIRI cuando el título tenía paréntesis/acentos — url(#fill-…)

  // inválido → el área caía al fill negro por defecto. useId garantiza unicidad

  // por instancia; se sanitiza porque useId emite ":" (inválido en funcIRI).

  const rawId = useId();

  const gradientId = `fill-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;

  const chartData = useMemo(() => {
    return points.map((p) => ({
      label: p.label,

      value: p.value,
    }));
  }, [points]);

  const chartConfig: ChartConfig = useMemo(
    () => ({
      value: {
        label: title,

        color: palette.border,
      },
    }),

    [title, palette.border],
  );

  if (points.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            className="py-6"
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer
          config={chartConfig}
          className="aspect-auto"
          style={{ height }}
        >
          <AreaChart
            accessibilityLayer
            data={chartData}
            margin={{ left: 12, right: 12, top: 12, bottom: 12 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={palette.border}
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor={palette.border}
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              strokeDasharray="3 3"
              className="stroke-muted"
            />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                // Shorten label if too long

                if (value.length > 10) {
                  return value.slice(0, 10) + "...";
                }

                return value;
              }}
              className="text-xs fill-muted-foreground"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => value.toLocaleString("es-AR")}
              className="text-xs fill-muted-foreground"
            />
            <ChartTooltip
              cursor={{ stroke: palette.border, strokeOpacity: 0.3 }}
              content={
                <ChartTooltipContent
                  indicator="line"
                  labelFormatter={(value) => value}
                  formatter={(value) => (
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{title}</span>
                      <span className="font-mono font-medium tabular-nums">
                        {typeof value === "number"
                          ? value.toLocaleString("es-AR")
                          : value}
                        {valueSuffix ? ` ${valueSuffix}` : ""}
                      </span>
                    </div>
                  )}
                />
              }
            />
            <Area
              dataKey="value"
              type="monotone"
              fill={`url(#${gradientId})`}
              stroke={palette.border}
              strokeWidth={2}
              connectNulls={false}
              dot={{
                fill: palette.point || palette.border,
                strokeWidth: 0,
                r: 3,
              }}
              activeDot={{ r: 5, fill: palette.border }}
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export type { ChartPoint, Palette, TimeSeriesChartCardProps };

export default TimeSeriesChartCard;
